import React, { useState, useEffect } from "react";
import axios from "axios";
import { X, ArrowRight, ArrowLeft, ClipboardCheck, Check, Loader2 } from "lucide-react";
import { services, brand } from "../mock";
import { useToast } from "../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONCERNS = [
  "Anxiety",
  "Depression",
  "Burnout",
  "Grief & loss",
  "Trauma",
  "Life transition",
  "Relationship struggles",
  "Boundaries",
  "Self-worth",
  "Spiritual growth",
  "Stress management",
  "Sleep concerns",
];

const HEARD_OPTIONS = [
  "Instagram",
  "TikTok",
  "Google search",
  "Friend / referral",
  "Amazon (a reader of one of my books)",
  "Podcast",
  "Other",
];

const STEP_LABELS = ["About You", "What Brings You", "Health History", "Goals & Consent"];

const IntakeModal = ({ open, onClose, preselectedService = "", serviceLookupKey = "", servicePrice = "" }) => {
  const { toast } = useToast();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [plan, setPlan] = useState("full"); // full | 2pay | 3pay
  const [form, setForm] = useState({
    // About You
    fullName: "",
    pronouns: "",
    email: "",
    phone: "",
    dob: "",
    address: "",
    emergencyName: "",
    emergencyPhone: "",
    // Interest
    service: preselectedService,
    availability: "",
    heardFrom: "",
    // Concerns
    concerns: [],
    story: "",
    urgencyLevel: "",
    // History
    inTherapy: "",
    onMedication: "",
    medicationList: "",
    pastTreatment: "",
    diagnoses: "",
    substanceUse: "",
    safetyConcerns: "",
    // Goals
    goals: "",
    pastHelped: "",
    // Consent
    consentScope: false,
    consentAttend: false,
    consentPayment: false,
    consentPrivacy: false,
    signature: "",
    signatureDate: new Date().toISOString().split("T")[0],
  });

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      setStep(0);
      setDone(false);
      setPlan("full");
      setForm((f) => ({ ...f, service: preselectedService || f.service }));
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open, preselectedService]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !submitting) onClose();
    };
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, submitting]);

  if (!open) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleConcern = (c) => {
    setForm((f) => ({
      ...f,
      concerns: f.concerns.includes(c)
        ? f.concerns.filter((x) => x !== c)
        : [...f.concerns, c],
    }));
  };

  const validateStep = () => {
    if (step === 0) {
      if (!form.fullName || !form.email || !form.phone) {
        toast({ title: "A few things missing", description: "Please complete name, email, and phone before continuing." });
        return false;
      }
    }
    if (step === 3) {
      if (!form.consentScope || !form.consentAttend || !form.consentPayment || !form.consentPrivacy) {
        toast({ title: "Please review the acknowledgments", description: "All acknowledgment boxes must be checked to submit." });
        return false;
      }
      if (!form.signature) {
        toast({ title: "Signature required", description: "Please type your full name as your electronic signature." });
        return false;
      }
    }
    return true;
  };

  const next = () => { if (validateStep()) setStep((s) => Math.min(3, s + 1)); };
  const back = () => setStep((s) => Math.max(0, s - 1));

  // Effective lookup key: prop OR resolved from services list based on selected service title
  const baseLookupKey = serviceLookupKey || (services.find((s) => s.title === form.service)?.stripeLookupKey || "");
  const basePrice = servicePrice || (services.find((s) => s.title === form.service)?.price || "");
  // Extract numeric price for plan math (e.g. "$125" → 125)
  const basePriceNum = (() => {
    const m = String(basePrice).match(/[\d.]+/);
    return m ? parseFloat(m[0]) : 0;
  })();
  const planLabels = {
    full: `Pay in full · ${basePrice}`,
    "2pay": `2 monthly payments · $${(basePriceNum / 2).toFixed(2)}/mo`,
    "3pay": `3 monthly payments · $${(basePriceNum / 3).toFixed(2)}/mo`,
  };
  const effectiveLookupKey = baseLookupKey ? (plan === "full" ? baseLookupKey : `${baseLookupKey}_${plan}`) : "";
  const effectivePrice = planLabels[plan] || basePrice;

  const submit = async (e) => {
    e.preventDefault();
    if (!validateStep()) return;
    setSubmitting(true);
    try {
      const payload = {
        fullName: form.fullName,
        pronouns: form.pronouns,
        email: form.email,
        phone: form.phone,
        dob: form.dob,
        address: form.address,
        emergencyName: form.emergencyName,
        emergencyPhone: form.emergencyPhone,
        service: form.service,
        availability: form.availability,
        heardFrom: form.heardFrom,
        concerns: form.concerns,
        story: form.story,
        urgencyLevel: form.urgencyLevel,
        inTherapy: form.inTherapy,
        onMedication: form.onMedication,
        medicationList: form.medicationList,
        pastTreatment: form.pastTreatment,
        diagnoses: form.diagnoses,
        substanceUse: form.substanceUse,
        safetyConcerns: form.safetyConcerns,
        goals: form.goals,
        pastHelped: form.pastHelped,
        consentScope: form.consentScope,
        consentAttend: form.consentAttend,
        consentPayment: form.consentPayment,
        consentPrivacy: form.consentPrivacy,
        signature: form.signature,
        signatureDate: form.signatureDate,
        // Payment: only sent for paid services
        service_lookup_key: effectiveLookupKey || null,
        origin_url: effectiveLookupKey ? window.location.origin : null,
      };
      const resp = await axios.post(`${API}/intake`, payload);
      if (!resp.data?.ok) throw new Error("Submission failed");

      // Paid service → redirect to Stripe checkout
      if (resp.data.checkout_url) {
        toast({
          title: "Taking you to secure checkout…",
          description: `${effectivePrice ? effectivePrice + " · " : ""}Payment holds your session.`,
        });
        window.location.href = resp.data.checkout_url;
        return;
      }
      // Free flow → success screen
      setDone(true);
      toast({
        title: "Intake received",
        description: `Thank you, ${form.fullName.split(" ")[0]}. Check your inbox — a welcome note from Devona is on its way.`,
      });
    } catch (err) {
      toast({
        title: "Something went wrong",
        description: err.response?.data?.detail || `Please try again in a moment, or email ${brand.email} directly.`,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const labelCls = "text-[10px] uppercase tracking-[0.28em] text-[#D4A85B]/85";
  const inputCls =
    "mt-2 w-full bg-transparent border-b border-[#D4A85B]/30 focus:border-[#D4A85B] outline-none py-2.5 text-[#EDE3D0] placeholder-[#EDE3D0]/40";
  const textareaCls = inputCls + " resize-none";

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-[#0A0714]/90 backdrop-blur-sm" onClick={() => !submitting && onClose()} />
      <div className="relative w-full max-w-3xl max-h-[92vh] overflow-y-auto rounded-3xl border border-[#D4A85B]/40 bg-[#12081E] shadow-[0_40px_100px_-20px_rgba(212,168,91,0.35)]">
        <div className="pointer-events-none absolute -top-24 -left-16 w-[300px] h-[300px] rounded-full bg-[#A36CFF]/25 blur-[100px]" />
        <div className="pointer-events-none absolute -bottom-24 -right-16 w-[320px] h-[320px] rounded-full bg-[#D4A85B]/15 blur-[100px]" />

        <button
          onClick={() => !submitting && onClose()}
          aria-label="Close"
          className="absolute top-5 right-5 w-9 h-9 rounded-full border border-[#D4A85B]/40 text-[#EDE3D0] hover:bg-[#D4A85B] hover:text-[#0A0714] flex items-center justify-center transition z-10"
        >
          <X size={16} />
        </button>

        <div className="relative p-7 md:p-10">
          {done ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-full bg-[#D4A85B]/15 border border-[#D4A85B] flex items-center justify-center text-[#D4A85B] mx-auto">
                <Check size={26} />
              </div>
              <h2 className="font-serif text-3xl md:text-4xl leading-tight mt-6 text-[#EDE3D0]">
                Your intake is <span className="italic text-[#D4A85B]">safely received.</span>
              </h2>
              <p className="mt-4 text-[#EDE3D0]/75 max-w-md mx-auto">
                Thank you for trusting me with your story. I&apos;ll personally review it and reach out within two business days to schedule your session.
              </p>
              <button
                onClick={onClose}
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#D4A85B] text-[#0A0714] px-7 py-3.5 text-xs uppercase tracking-[0.28em] hover:bg-[#E9C77B] transition"
              >
                Close <ArrowRight size={14} />
              </button>
            </div>
          ) : (
            <>
              <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] text-[#D4A85B]/85">
                <ClipboardCheck size={12} /> Client Intake Form
              </div>
              <h2 className="font-serif text-3xl md:text-4xl leading-tight mt-4 text-[#EDE3D0]">
                Tell me about
                <span className="italic text-[#D4A85B]"> where you are.</span>
              </h2>
              <p className="mt-3 text-sm text-[#EDE3D0]/70 leading-relaxed max-w-md">
                This confidential intake helps me hold you well from our very first session. It takes about 5 minutes.
              </p>

              {/* Progress */}
              <div className="mt-8 mb-8">
                <div className="flex items-center gap-2">
                  {STEP_LABELS.map((label, i) => (
                    <div key={i} className="flex items-center gap-2 flex-1">
                      <div className={`h-1 flex-1 rounded-full transition ${i <= step ? "bg-[#D4A85B]" : "bg-[#D4A85B]/15"}`} />
                    </div>
                  ))}
                </div>
                <div className="mt-3 flex items-center justify-between text-[10px] uppercase tracking-[0.28em]">
                  <span className="text-[#D4A85B]">Step {step + 1} of 4 · {STEP_LABELS[step]}</span>
                </div>
              </div>

              <form onSubmit={submit} className="space-y-8">
                {step === 0 && (
                  <div className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className={labelCls}>Full name *</label>
                        <input value={form.fullName} onChange={(e) => set("fullName", e.target.value)} className={inputCls} placeholder="First & last name" />
                      </div>
                      <div>
                        <label className={labelCls}>Pronouns</label>
                        <input value={form.pronouns} onChange={(e) => set("pronouns", e.target.value)} className={inputCls} placeholder="she/her, they/them, etc." />
                      </div>
                      <div>
                        <label className={labelCls}>Email *</label>
                        <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} placeholder="you@email.com" />
                      </div>
                      <div>
                        <label className={labelCls}>Phone *</label>
                        <input type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} placeholder="(555) 000-0000" />
                      </div>
                      <div>
                        <label className={labelCls}>Date of birth</label>
                        <input type="date" value={form.dob} onChange={(e) => set("dob", e.target.value)} className={inputCls} />
                      </div>
                      <div>
                        <label className={labelCls}>City / state (optional)</label>
                        <input value={form.address} onChange={(e) => set("address", e.target.value)} className={inputCls} placeholder="Atlanta, GA" />
                      </div>
                    </div>

                    <div className="pt-4 border-t border-[#D4A85B]/15">
                      <div className={labelCls}>Emergency contact (optional)</div>
                      <div className="grid md:grid-cols-2 gap-6 mt-2">
                        <input value={form.emergencyName} onChange={(e) => set("emergencyName", e.target.value)} className={inputCls + " mt-0"} placeholder="Name & relationship" />
                        <input type="tel" value={form.emergencyPhone} onChange={(e) => set("emergencyPhone", e.target.value)} className={inputCls + " mt-0"} placeholder="Phone" />
                      </div>
                    </div>
                  </div>
                )}

                {step === 1 && (
                  <div className="space-y-6">
                    <div>
                      <label className={labelCls}>Which offering are you drawn to?</label>
                      <select value={form.service} onChange={(e) => set("service", e.target.value)} className={inputCls}>
                        <option value="" className="bg-[#12081E]">Choose one (optional)</option>
                        {services.map((s) => (
                          <option key={s.id} value={s.title} className="bg-[#12081E]">{s.title} · {s.price}</option>
                        ))}
                        <option value="Not sure yet" className="bg-[#12081E]">I&apos;m not sure yet</option>
                      </select>
                    </div>

                    <div>
                      <label className={labelCls}>Best days & times to meet</label>
                      <input value={form.availability} onChange={(e) => set("availability", e.target.value)} className={inputCls} placeholder="Weekdays after 5pm ET, weekends anytime, etc." />
                    </div>

                    <div>
                      <label className={labelCls}>How did you find me?</label>
                      <select value={form.heardFrom} onChange={(e) => set("heardFrom", e.target.value)} className={inputCls}>
                        <option value="" className="bg-[#12081E]">Select one</option>
                        {HEARD_OPTIONS.map((o) => (<option key={o} value={o} className="bg-[#12081E]">{o}</option>))}
                      </select>
                    </div>

                    <div>
                      <label className={labelCls}>What are you working through right now?</label>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {CONCERNS.map((c) => {
                          const active = form.concerns.includes(c);
                          return (
                            <button
                              key={c}
                              type="button"
                              onClick={() => toggleConcern(c)}
                              className={`text-xs px-3 py-1.5 rounded-full border transition ${
                                active
                                  ? "bg-[#D4A85B] border-[#D4A85B] text-[#0A0714]"
                                  : "border-[#D4A85B]/30 text-[#EDE3D0]/85 hover:border-[#D4A85B]"
                              }`}
                            >
                              {c}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div>
                      <label className={labelCls}>In your own words, what brings you here?</label>
                      <textarea rows={4} value={form.story} onChange={(e) => set("story", e.target.value)} className={textareaCls} placeholder="Share as much or as little as feels right." />
                    </div>

                    <div>
                      <label className={labelCls}>How urgent does this feel to you?</label>
                      <select value={form.urgencyLevel} onChange={(e) => set("urgencyLevel", e.target.value)} className={inputCls}>
                        <option value="" className="bg-[#12081E]">Select one</option>
                        <option value="Ready when you are" className="bg-[#12081E]">Ready when you are — no rush</option>
                        <option value="Would love to start soon" className="bg-[#12081E]">Would love to start soon</option>
                        <option value="Feeling urgent" className="bg-[#12081E]">Feeling urgent</option>
                        <option value="In crisis — need to speak sooner" className="bg-[#12081E]">In crisis — need to speak sooner</option>
                      </select>
                    </div>
                  </div>
                )}

                {step === 2 && (
                  <div className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className={labelCls}>Currently in therapy or counseling?</label>
                        <select value={form.inTherapy} onChange={(e) => set("inTherapy", e.target.value)} className={inputCls}>
                          <option value="" className="bg-[#12081E]">Select</option>
                          <option value="Yes" className="bg-[#12081E]">Yes</option>
                          <option value="No" className="bg-[#12081E]">No</option>
                          <option value="Recently ended" className="bg-[#12081E]">Recently ended</option>
                        </select>
                      </div>
                      <div>
                        <label className={labelCls}>On psychiatric medication?</label>
                        <select value={form.onMedication} onChange={(e) => set("onMedication", e.target.value)} className={inputCls}>
                          <option value="" className="bg-[#12081E]">Select</option>
                          <option value="Yes" className="bg-[#12081E]">Yes</option>
                          <option value="No" className="bg-[#12081E]">No</option>
                          <option value="Prefer not to say" className="bg-[#12081E]">Prefer not to say</option>
                        </select>
                      </div>
                    </div>

                    {form.onMedication === "Yes" && (
                      <div>
                        <label className={labelCls}>Medications (name & purpose if comfortable sharing)</label>
                        <textarea rows={2} value={form.medicationList} onChange={(e) => set("medicationList", e.target.value)} className={textareaCls} placeholder="e.g. Zoloft 50mg — anxiety" />
                      </div>
                    )}

                    <div>
                      <label className={labelCls}>Any past mental-health treatment or diagnoses (optional)</label>
                      <textarea rows={2} value={form.pastTreatment} onChange={(e) => set("pastTreatment", e.target.value)} className={textareaCls} placeholder="Only what you'd like me to know" />
                    </div>

                    <div>
                      <label className={labelCls}>Any current diagnoses you&apos;d like me to know about?</label>
                      <input value={form.diagnoses} onChange={(e) => set("diagnoses", e.target.value)} className={inputCls} placeholder="Optional" />
                    </div>

                    <div>
                      <label className={labelCls}>Substance use (alcohol, cannabis, other) — optional</label>
                      <input value={form.substanceUse} onChange={(e) => set("substanceUse", e.target.value)} className={inputCls} placeholder="Frequency / type, if you'd like to share" />
                    </div>

                    <div className="pt-4 border-t border-[#D4A85B]/15">
                      <label className={labelCls}>Any current thoughts of self-harm or suicide?</label>
                      <select value={form.safetyConcerns} onChange={(e) => set("safetyConcerns", e.target.value)} className={inputCls}>
                        <option value="" className="bg-[#12081E]">Select</option>
                        <option value="No" className="bg-[#12081E]">No</option>
                        <option value="Passive thoughts sometimes" className="bg-[#12081E]">Passive thoughts sometimes</option>
                        <option value="Active thoughts — I'd like support" className="bg-[#12081E]">Active thoughts — I&apos;d like support</option>
                      </select>
                      <p className="mt-3 text-[11px] text-[#EDE3D0]/55 italic leading-relaxed">
                        If you are in immediate crisis, please dial <strong className="text-[#D4A85B] not-italic">988</strong> (Suicide & Crisis Lifeline) or go to your nearest emergency room. My intake response is not a substitute for emergency care.
                      </p>
                    </div>
                  </div>
                )}

                {step === 3 && (
                  <div className="space-y-6">
                    <div>
                      <label className={labelCls}>What are you hoping to gain from working together?</label>
                      <textarea rows={3} value={form.goals} onChange={(e) => set("goals", e.target.value)} className={textareaCls} placeholder="Your goals, dreams, and desired outcomes" />
                    </div>
                    <div>
                      <label className={labelCls}>What has helped in the past?</label>
                      <textarea rows={2} value={form.pastHelped} onChange={(e) => set("pastHelped", e.target.value)} className={textareaCls} placeholder="Practices, tools, or people that have supported you" />
                    </div>

                    {baseLookupKey && basePriceNum > 0 && (
                      <div className="pt-4 border-t border-[#D4A85B]/15">
                        <div className={labelCls}>Choose your payment plan</div>
                        <p className="text-xs text-[#EDE3D0]/60 mt-2 mb-4">Every offering includes interest-free installments. Your seat is only held once the first payment clears.</p>
                        <div className="space-y-2.5">
                          {[
                            { id: "full", title: "Pay in full", sub: basePrice, hint: "One charge, done." },
                            { id: "2pay", title: "2 monthly payments", sub: `$${(basePriceNum / 2).toFixed(2)}/mo · total ${basePrice}`, hint: "Charged today and again in 30 days." },
                            { id: "3pay", title: "3 monthly payments", sub: `$${(basePriceNum / 3).toFixed(2)}/mo · total ${basePrice}`, hint: "Charged today, then twice more over 60 days." },
                          ].map((opt) => (
                            <label
                              key={opt.id}
                              data-testid={`plan-${opt.id}`}
                              className={`flex items-start gap-3 cursor-pointer rounded-2xl border p-4 transition ${
                                plan === opt.id ? "border-[#D4A85B] bg-[#D4A85B]/[0.08]" : "border-[#D4A85B]/25 hover:border-[#D4A85B]/60"
                              }`}
                            >
                              <input
                                type="radio"
                                name="plan"
                                value={opt.id}
                                checked={plan === opt.id}
                                onChange={() => setPlan(opt.id)}
                                className="mt-1 accent-[#D4A85B]"
                              />
                              <div className="flex-1 flex items-start justify-between gap-4">
                                <div>
                                  <div className="text-sm text-[#EDE3D0] font-medium">{opt.title}</div>
                                  <div className="text-[11px] text-[#EDE3D0]/60 mt-0.5">{opt.hint}</div>
                                </div>
                                <div className="text-sm text-[#D4A85B] font-serif whitespace-nowrap">{opt.sub}</div>
                              </div>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="pt-4 border-t border-[#D4A85B]/15 space-y-4">
                      <div className={labelCls}>Acknowledgments</div>

                      <label className="flex items-start gap-3 cursor-pointer rounded-2xl border border-[#D4A85B]/40 bg-[#D4A85B]/[0.04] p-4">
                        <input type="checkbox" checked={form.consentScope} onChange={(e) => set("consentScope", e.target.checked)} className="mt-1 accent-[#D4A85B]" />
                        <span className="text-sm text-[#EDE3D0]/90 leading-relaxed">
                          <span className="text-[#D4A85B] font-medium">Scope of practice.</span> I understand that coaching sessions with Mindful Matters Coaching are <strong className="text-[#EDE3D0]">not counseling or psychotherapy</strong>, are not clinical treatment, and are not intended to diagnose, treat, or cure any mental-health or medical condition. I understand coaching is future-focused and does not replace care from a licensed mental-health professional or physician, and I will seek appropriate clinical care as needed.
                        </span>
                      </label>

                      <label className="flex items-start gap-3 cursor-pointer">
                        <input type="checkbox" checked={form.consentAttend} onChange={(e) => set("consentAttend", e.target.checked)} className="mt-1 accent-[#D4A85B]" />
                        <span className="text-sm text-[#EDE3D0]/85">I understand this intake begins a coaching relationship and I intend to attend the session I schedule. I will provide at least 24 hours notice if I need to reschedule.</span>
                      </label>
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input type="checkbox" checked={form.consentPayment} onChange={(e) => set("consentPayment", e.target.checked)} className="mt-1 accent-[#D4A85B]" />
                        <span className="text-sm text-[#EDE3D0]/85">I understand session fees are due at the time of scheduling and no-shows may be charged in full.</span>
                      </label>
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input type="checkbox" checked={form.consentPrivacy} onChange={(e) => set("consentPrivacy", e.target.checked)} className="mt-1 accent-[#D4A85B]" />
                        <span className="text-sm text-[#EDE3D0]/85">I understand my information is held in strict confidence and only used to prepare for and deliver my sessions.</span>
                      </label>
                    </div>

                    <div className="pt-4 border-t border-[#D4A85B]/15 grid md:grid-cols-2 gap-6">
                      <div>
                        <label className={labelCls}>Electronic signature *</label>
                        <input value={form.signature} onChange={(e) => set("signature", e.target.value)} className={inputCls} placeholder="Type your full legal name" style={{ fontFamily: "'Kaushan Script', cursive", fontSize: "22px" }} />
                      </div>
                      <div>
                        <label className={labelCls}>Date</label>
                        <input type="date" value={form.signatureDate} onChange={(e) => set("signatureDate", e.target.value)} className={inputCls} />
                      </div>
                    </div>
                  </div>
                )}

                <div className="pt-6 border-t border-[#D4A85B]/15 flex items-center justify-between gap-4">
                  <button
                    type="button"
                    onClick={back}
                    disabled={step === 0}
                    className={`inline-flex items-center gap-2 rounded-full border border-[#D4A85B]/50 text-[#EDE3D0] px-5 py-3 text-xs uppercase tracking-[0.28em] transition ${step === 0 ? "opacity-30 cursor-not-allowed" : "hover:border-[#D4A85B] hover:text-[#D4A85B]"}`}
                  >
                    <ArrowLeft size={13} /> Back
                  </button>

                  {step < 3 ? (
                    <button
                      type="button"
                      onClick={next}
                      className="inline-flex items-center gap-2 rounded-full bg-[#D4A85B] text-[#0A0714] px-6 py-3 text-xs uppercase tracking-[0.28em] hover:bg-[#E9C77B] transition"
                    >
                      Continue <ArrowRight size={13} />
                    </button>
                  ) : (
                    <button
                      type="submit"
                      disabled={submitting}
                      className="inline-flex items-center gap-2 rounded-full bg-[#D4A85B] text-[#0A0714] px-6 py-3 text-xs uppercase tracking-[0.28em] hover:bg-[#E9C77B] transition disabled:opacity-70"
                    >
                      {submitting ? (<><Loader2 size={13} className="animate-spin" /> Sending</>) : (<>{effectiveLookupKey ? `Continue to payment · ${plan === "full" ? basePrice : `$${(basePriceNum / (plan === "2pay" ? 2 : 3)).toFixed(2)}/mo`}` : "Submit intake"} <ArrowRight size={13} /></>)}
                    </button>
                  )}
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default IntakeModal;
